import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

const PRIORITY_CONFIG: Record<number, { label: string; color: string; bg: string }> = {
  0: { label: 'P0', color: 'var(--status-critical)', bg: 'var(--status-critical-muted)' },
  1: { label: 'P1', color: 'var(--status-warning)', bg: 'var(--status-warning-muted)' },
  2: { label: 'P2', color: 'var(--status-warning)', bg: 'var(--status-warning-muted)' },
  3: { label: 'P3', color: 'var(--accent-text)', bg: 'var(--accent-muted)' },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  theft_shoplifting: 'Theft / Shoplifting',
  robbery: 'Robbery',
  safety_hazard: 'Safety Hazard',
  employee_issue: 'Employee Issue',
  suspicious_activity: 'Suspicious Activity',
  customer_incident: 'Customer Incident',
  maintenance: 'Maintenance',
  delivery: 'Delivery',
  cash_handling: 'Cash Handling',
  shift_change: 'Shift Change',
  fuel_incident: 'Fuel Incident',
  custom: 'Custom',
  theft: 'Theft',
  employee_theft: 'Employee Theft',
  no_show: 'No Show',
  late_arrival: 'Late Arrival',
  early_departure: 'Early Departure',
  unauthorized_area: 'Unauthorized Area',
  safety_violation: 'Safety Violation',
  property_damage: 'Property Damage',
  maintenance_issue: 'Maintenance Issue',
  inventory: 'Inventory',
  other: 'Other',
};

interface Employee {
  id: number;
  full_name: string;
  role: string;
  phone?: string;
  photo_url?: string;
  status?: string;
}

interface AlertItem {
  id: number;
  title: string;
  priority: number;
  event_type: string;
  description: string;
  camera_location: string | null;
  created_at: string;
  attachments: any[];
}

interface ReportItem {
  id: number;
  title: string;
  report_date: string;
  report_type: string;
  status: string;
  created_at: string;
}

const REPORT_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: 'var(--status-warning)', bg: 'var(--status-warning-muted)' },
  published: { label: 'Published', color: 'var(--status-success)', bg: 'var(--status-success-muted)' },
};

function formatReportDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatEventType(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getInitials(name: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const ROLE_LABELS: Record<string, string> = {
  cashier: 'Cashier',
  shift_lead: 'Shift Lead',
  stock_clerk: 'Stock Clerk',
  manager: 'Manager',
};

function formatRole(role: string): string {
  return ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function StoreDetailPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();

  const storeName = (window.history.state?.usr?.storeName as string) || `Store #${storeId}`;

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get(`/stores/${storeId}/employees`);
      setEmployees(res.data.employees);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load employees.'));
    } finally {
      setEmployeesLoading(false);
    }
  }, [storeId]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get(`/monitors/stores/${storeId}/alerts`);
      setAlerts(res.data.alerts);
    } catch (err: any) {
      if (!error) setError(extractErrorMessage(err, 'Failed to load alerts.'));
    } finally {
      setAlertsLoading(false);
    }
  }, [storeId, error]);

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get(`/stores/${storeId}/reports`);
      const items = res.data.items || res.data.reports || [];
      setReports(items.slice(0, 5));
    } catch {
      // silently fail — reports section is supplemental
    } finally {
      setReportsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchEmployees();
    fetchAlerts();
    fetchReports();
  }, [fetchEmployees, fetchAlerts, fetchReports]);

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate('/')} style={styles.backButton}>
          ← Back to Dashboard
        </button>

        <div style={styles.storeHeader}>
          <h2 style={styles.storeName}>{storeName}</h2>
          <div style={styles.actionButtons}>
            <Link to={`/stores/${storeId}/create-alert`} style={styles.createAlertButton}>
              + Create Alert
            </Link>
            <Link to={`/stores/${storeId}/create-report`} style={styles.createReportButton}>
              + Create Report
            </Link>
            <Link to={`/stores/${storeId}/employees/new`} style={styles.addEmployeeButton}>
              + Add Employee
            </Link>
          </div>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* Employees Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Employees</h3>
          {employeesLoading ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>Loading employees...</p>
            </div>
          ) : employees.length === 0 ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>No employees added yet.</p>
            </div>
          ) : (
            <div style={styles.employeeList}>
              {employees.map((emp) => (
                <div
                  key={emp.id}
                  style={styles.employeeCard}
                  onClick={() => navigate(`/employees/${emp.id}`)}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface-raised)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)'; }}
                >
                  {emp.photo_url ? (
                    <img src={emp.photo_url} alt={emp.full_name} style={styles.avatar} />
                  ) : (
                    <div style={styles.avatarInitials}>{getInitials(emp.full_name)}</div>
                  )}
                  <div style={styles.employeeInfo}>
                    <span style={styles.employeeName}>{emp.full_name}</span>
                    <span style={styles.employeeRole}>{formatRole(emp.role)}</span>
                  </div>
                  <span style={styles.chevron}>›</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alerts Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Recent Alerts</h3>
          {alertsLoading ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>Loading alerts...</p>
            </div>
          ) : alerts.length === 0 ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>No alerts yet for this store.</p>
            </div>
          ) : (
            <div style={styles.alertList}>
              {alerts.map((alert) => {
                const pConfig = PRIORITY_CONFIG[alert.priority] || PRIORITY_CONFIG[3];
                return (
                  <div key={alert.id} style={styles.alertCard}>
                    <div style={styles.alertTop}>
                      <span style={{
                        ...styles.priorityBadge,
                        background: pConfig.bg,
                        color: pConfig.color,
                        borderColor: pConfig.color,
                      }}>
                        {pConfig.label}
                      </span>
                      <span style={styles.eventType}>{formatEventType(alert.event_type)}</span>
                      <span style={styles.timestamp}>{formatTimestamp(alert.created_at)}</span>
                    </div>
                    <h3 style={styles.alertTitle}>{alert.title}</h3>
                    <p style={styles.alertDescription}>{alert.description}</p>
                    {alert.camera_location && (
                      <div style={styles.cameraLocation}>
                        <span style={styles.cameraIcon}>&#9673;</span> {alert.camera_location}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Reports Section */}
        <div style={styles.section}>
          <div style={styles.reportsSectionHeader}>
            <h3 style={styles.sectionTitle}>Recent Reports</h3>
            <Link to={`/stores/${storeId}/reports`} style={styles.viewAllLink}>
              View All →
            </Link>
          </div>
          {reportsLoading ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>No reports yet for this store.</p>
            </div>
          ) : (
            <div style={styles.reportList}>
              {reports.map((report) => {
                const sConfig = REPORT_STATUS_CONFIG[report.status] || REPORT_STATUS_CONFIG.draft;
                return (
                  <div
                    key={report.id}
                    style={styles.reportCard}
                    onClick={() => navigate(`/reports/${report.id}`)}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.3)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
                  >
                    <div style={styles.reportCardTop}>
                      <span style={{
                        ...styles.reportStatusBadge,
                        background: sConfig.bg,
                        color: sConfig.color,
                      }}>
                        {sConfig.label}
                      </span>
                      <span style={styles.reportCardDate}>{formatReportDate(report.report_date)}</span>
                    </div>
                    <h3 style={styles.reportCardTitle}>{report.title}</h3>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 960,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 'var(--space-6)',
  },
  storeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-8)',
  },
  storeName: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 700,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: 0,
  },
  actionButtons: {
    display: 'flex',
    gap: 10,
  },
  createAlertButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 20px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--status-critical)',
    color: '#fff',
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    textDecoration: 'none',
  },
  createReportButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 20px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    textDecoration: 'none',
  },
  addEmployeeButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 20px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-surface-raised)',
    border: '1px solid var(--border-default)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    textDecoration: 'none',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
    marginBottom: 'var(--space-5)',
  },
  section: {
    marginBottom: 'var(--space-8)',
  },
  sectionTitle: {
    fontSize: 'var(--text-lg)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 14px',
  },
  placeholder: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '48px 24px',
    textAlign: 'center',
  },
  placeholderText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    margin: 0,
  },
  // Employee styles
  employeeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  employeeCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    padding: '14px 18px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    objectFit: 'cover',
  },
  avatarInitials: {
    width: 40,
    height: 40,
    borderRadius: 20,
    background: 'var(--accent-muted)',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  employeeInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },
  employeeName: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
  },
  employeeRole: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
  chevron: {
    fontSize: 20,
    color: 'var(--text-tertiary)',
    fontWeight: 300,
  },
  // Alert styles
  alertList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  alertCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '20px 24px',
  },
  alertTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  priorityBadge: {
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    border: '1px solid',
    letterSpacing: 0.5,
  },
  eventType: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    background: 'var(--bg-surface-raised)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
  },
  timestamp: {
    marginLeft: 'auto',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-tertiary)',
  },
  alertTitle: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    margin: '0 0 6px',
  },
  alertDescription: {
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cameraLocation: {
    marginTop: 10,
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-tertiary)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  cameraIcon: {
    fontSize: 10,
    color: 'var(--text-tertiary)',
  },
  // Report styles
  reportsSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  viewAllLink: {
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--accent-text)',
    textDecoration: 'none',
  },
  reportList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  reportCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    padding: '16px 20px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  reportCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  reportStatusBadge: {
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    letterSpacing: 0.5,
  },
  reportCardDate: {
    marginLeft: 'auto',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-tertiary)',
  },
  reportCardTitle: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    margin: 0,
  },
};
